import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  STUDENT_CODE_LIMITS,
  studentCodeExample,
  studentCodeFormatError,
  type StudentCodeFormat,
} from '@dosfilos/domain';
import { tenantConfigApi } from '../../../lib/api/tenant-config.api';
import { errorText } from '../../../lib/error-text';
import { useTenantConfig } from '../../../hooks/use-tenant-config';

// ─── El formato del código de alumno (H6) ───────────────────────────────────
//
// Prefijo, aleatorio o correlativo, dígitos y —solo el correlativo— si lleva el
// año. Lo guarda `updateTenantConfig` (administración del seminario o
// plataforma), que lo valida y lo audita. SOLO AFECTA A LOS CÓDIGOS NUEVOS: los
// ya asignados no se reescriben nunca.

const same = (a: StudentCodeFormat, b: StudentCodeFormat) =>
  a.prefix === b.prefix &&
  a.strategy === b.strategy &&
  a.digits === b.digits &&
  !!a.includeYear === !!b.includeYear;

export function StudentCodeEditor({ tenantId }: { tenantId: string }): JSX.Element {
  const { t } = useTranslation('sis');
  const queryClient = useQueryClient();
  const tenantConfig = useTenantConfig();
  const saved = tenantConfig.studentCodeFormat;
  // SIN FORMATO GUARDADO NO SE ASIGNA NINGÚN CÓDIGO: la pantalla lo dice, y
  // guardar el de partida también cuenta como elegirlo.
  const configured = tenantConfig.studentCodeConfigured;
  const [draft, setDraft] = useState<StudentCodeFormat>(saved);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setDraft(saved), [saved]);

  const problem = useMemo(() => studentCodeFormatError(draft), [draft]);
  const example = problem ? '—' : studentCodeExample(draft, new Date().getFullYear());

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await tenantConfigApi.updateTenantConfig({
        tenantId,
        studentCode: {
          prefix: draft.prefix,
          strategy: draft.strategy,
          digits: draft.digits,
          ...(draft.strategy === 'sequential' && draft.includeYear ? { includeYear: true } : {}),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['tenant-config-view', tenantId] });
      setMsg({ ok: true, text: t('settings.studentCode.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('settings.studentCode.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {t('settings.studentCode.desc')}
      </Typography>
      {!configured && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t('settings.studentCode.notConfigured')}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        <TextField
          size="small"
          label={t('settings.studentCode.prefix')}
          value={draft.prefix}
          onChange={(e) => setDraft((d) => ({ ...d, prefix: e.target.value }))}
          helperText={t('settings.studentCode.prefixHelp', { max: STUDENT_CODE_LIMITS.prefixMax })}
          sx={{ maxWidth: 180 }}
        />
        <TextField
          select
          size="small"
          label={t('settings.studentCode.strategy')}
          value={draft.strategy}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              strategy: e.target.value as StudentCodeFormat['strategy'],
              ...(e.target.value === 'random' ? { includeYear: false } : {}),
            }))
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="random">{t('settings.studentCode.random')}</MenuItem>
          <MenuItem value="sequential">{t('settings.studentCode.sequential')}</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="number"
          label={t('settings.studentCode.digits')}
          value={draft.digits}
          onChange={(e) => setDraft((d) => ({ ...d, digits: Number(e.target.value) }))}
          inputProps={{ min: STUDENT_CODE_LIMITS.digitsMin, max: STUDENT_CODE_LIMITS.digitsMax }}
          sx={{ maxWidth: 120 }}
        />
        {draft.strategy === 'sequential' && (
          <FormControlLabel
            control={
              <Switch
                checked={!!draft.includeYear}
                onChange={(e) => setDraft((d) => ({ ...d, includeYear: e.target.checked }))}
              />
            }
            label={t('settings.studentCode.includeYear')}
          />
        )}
      </Stack>
      <Typography variant="body2" sx={{ mt: 1.5 }}>
        {t('settings.studentCode.example', { code: example })}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {t('settings.studentCode.onlyNew')}
      </Typography>
      {problem && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t(`errors.${problem}`, { ns: 'common', max: STUDENT_CODE_LIMITS.prefixMax })}
        </Alert>
      )}
      {msg && (
        <Alert severity={msg.ok ? 'success' : 'error'} sx={{ mt: 1.5 }}>
          {msg.text}
        </Alert>
      )}
      <Button
        variant="contained"
        size="small"
        sx={{ mt: 1.5 }}
        disabled={busy || !!problem || (configured && same(draft, saved))}
        onClick={() => void save()}
      >
        {t('settings.studentCode.save')}
      </Button>
    </Box>
  );
}
